import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/auth/ProtectedRoute";
import NotFound from "pages/NotFound";
import AdminDashboard from './pages/admin-dashboard';
import LeagueManagement from './pages/league-management';
import TeamRegistration from './pages/team-registration';
import TeamManagementPage from './pages/team-management';
import LoginPage from './pages/login';
import MatchManagement from './pages/match-management';
import TeamProfiles from './pages/team-profiles';
import PlayerRegistration from './pages/player-registration';
import PublicLeaguePortal from './pages/public-league-portal';
import PlayerStatistics from './pages/player-statistics';
import UserManagementPage from './pages/user-management';
import ApprovalsPage from './pages/approvals';
import ProfilePage from './pages/profile';
import EmailConfigurationPage from './pages/email-configuration';
import ReportsPage from './pages/reports';
import ForgotPasswordPage from './pages/forgot-password';
import ResetPasswordPage from './pages/reset-password';
import AuthConfirmPage from './pages/auth-confirm';
import PlayersProfilesPage from './pages/players-profiles';
import LeagueSetup from './pages/league-setup';
import LeagueOrganizer from './pages/league-organizer';
import OfficialRegistration from './pages/official-registration';
import CoachRegistration from './pages/coach-registration';
import CoachSelfRegistrationPage from './pages/coach-self-registration';
import LeagueCoaches from './pages/league-coaches';
import LeagueOfficials from './pages/league-officials';
import GamesManagement from './pages/games-management';
import MatchCenter from './pages/match-center';
import MatchesUpdatesPage from './pages/matches-updates';
import RegionAssociationRegistration from './pages/region-association-registration';
import RegionalTeamRegistration from './pages/regional-team-registration';
import RegionalTeamPlayersRegistration from './pages/regional-team-players-registration';
import TournamentGamesOrganizer from './pages/tournament-games-organizer';
import TournamentOfficials from './pages/tournament-officials';
import RegionalTeamsProfiles from './pages/regional-teams-profiles';
import RegionalTeamPlayerStatistics from './pages/regional-team-player-statistics';
import RegionsListPage from './pages/regions';
import RegionDetailPage from './pages/regions/detail';
import ClubsListPage from './pages/clubs';
import ClubDetailPage from './pages/clubs/detail';
import BrowsePlayersPage from './pages/browse-players';
import PlayerDetailPage from './pages/browse-players/detail';
import PublicStatsPage from './pages/public-stats';
import LeaguesListPage from './pages/leagues';
import LeagueDetailPage from './pages/leagues/detail';
import TournamentsListPage from './pages/tournaments';
import TournamentDetailPage from './pages/tournaments/detail';
import RegionalTeamDetailPage from './pages/regional-teams/detail';
import RegionalPlayerDetailPage from './pages/regional-players/detail';
import PrivacyPolicyPage from './pages/privacy-policy';
import TermsOfServicePage from './pages/terms-of-service';
import FinanceDashboard from './pages/finance-dashboard';
import RegionFinance from './pages/region-finance';
import MembershipRecords from './pages/membership-records';
import { PERMISSIONS } from './constants/accessControl';

const protect = (element, permission) => (
  <ProtectedRoute permission={permission}>{element}</ProtectedRoute>
);

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        <Route path="/" element={<PublicLeaguePortal />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/confirm" element={<AuthConfirmPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/coach-register/:token" element={<CoachSelfRegistrationPage />} />
        <Route path="/public-league-portal" element={<PublicLeaguePortal />} />
        <Route path="/match-center" element={<MatchCenter />} />
        <Route path="/regions" element={<RegionsListPage />} />
        <Route path="/regions/:id" element={<RegionDetailPage />} />
        <Route path="/regional-teams/:id" element={<RegionalTeamDetailPage />} />
        <Route path="/regional-players/:id" element={<RegionalPlayerDetailPage />} />
        <Route path="/clubs" element={<ClubsListPage />} />
        <Route path="/clubs/:id" element={<ClubDetailPage />} />
        <Route path="/browse-players" element={<BrowsePlayersPage />} />
        <Route path="/browse-players/:id" element={<PlayerDetailPage />} />
        <Route path="/public-stats" element={<PublicStatsPage />} />
        <Route path="/leagues" element={<LeaguesListPage />} />
        <Route path="/leagues/:id" element={<LeagueDetailPage />} />
        <Route path="/tournaments" element={<TournamentsListPage />} />
        <Route path="/tournaments/:id" element={<TournamentDetailPage />} />

        <Route path="/admin-dashboard" element={protect(<AdminDashboard />, PERMISSIONS.DASHBOARD_VIEW)} />
        <Route path="/profile" element={protect(<ProfilePage />, PERMISSIONS.DASHBOARD_VIEW)} />
        <Route path="/region-association-registration" element={protect(<RegionAssociationRegistration />, PERMISSIONS.REGISTRATION_REGION_ASSOCIATION)} />
        <Route path="/regional-team-registration" element={protect(<RegionalTeamRegistration />, PERMISSIONS.REGISTRATION_REGIONAL_TEAM)} />
        <Route path="/regional-team-players-registration" element={protect(<RegionalTeamPlayersRegistration />, PERMISSIONS.REGISTRATION_REGIONAL_PLAYERS)} />
        <Route path="/team-registration" element={protect(<TeamRegistration />, PERMISSIONS.REGISTRATION_CLUB)} />
        <Route path="/player-registration" element={protect(<PlayerRegistration />, PERMISSIONS.REGISTRATION_PLAYER)} />
        <Route path="/official-registration" element={protect(<OfficialRegistration />, PERMISSIONS.REGISTRATION_OFFICIAL)} />
        <Route path="/coach-registration" element={protect(<CoachRegistration />, PERMISSIONS.REGISTRATION_COACH)} />
        <Route path="/tournament-games-organizer" element={protect(<TournamentGamesOrganizer />, PERMISSIONS.TOURNAMENT_GAMES)} />
        <Route path="/tournament-officials" element={protect(<TournamentOfficials />, PERMISSIONS.TOURNAMENT_OFFICIALS)} />
        <Route path="/regional-teams-profiles" element={protect(<RegionalTeamsProfiles />, PERMISSIONS.TOURNAMENT_REGIONAL_TEAMS)} />
        <Route path="/regional-team-player-statistics" element={protect(<RegionalTeamPlayerStatistics />, PERMISSIONS.TOURNAMENT_PLAYER_STATS)} />
        <Route path="/games-management" element={protect(<GamesManagement />, PERMISSIONS.LEAGUE_GAMES)} />
        <Route path="/league-organizer" element={protect(<LeagueOrganizer />, PERMISSIONS.LEAGUE_ORGANIZER)} />
        <Route path="/league-management" element={protect(<LeagueManagement />, PERMISSIONS.LEAGUE_ORGANIZER)} />
        <Route path="/league-setup" element={protect(<LeagueSetup />, PERMISSIONS.LEAGUE_ORGANIZER)} />
        <Route path="/league-setup/:id" element={protect(<LeagueSetup />, PERMISSIONS.LEAGUE_ORGANIZER)} />
        <Route path="/league-officials" element={protect(<LeagueOfficials />, PERMISSIONS.LEAGUE_OFFICIALS)} />
        <Route path="/league-coaches" element={protect(<LeagueCoaches />, PERMISSIONS.LEAGUE_COACHES)} />
        <Route path="/team-management" element={protect(<TeamManagementPage />, PERMISSIONS.LEAGUE_TEAMS)} />
        <Route path="/team-profiles" element={protect(<TeamProfiles />, PERMISSIONS.LEAGUE_TEAMS)} />
        <Route path="/players-profiles" element={protect(<PlayersProfilesPage />, PERMISSIONS.LEAGUE_PLAYERS)} />
        <Route path="/player-statistics" element={protect(<PlayerStatistics />, PERMISSIONS.LEAGUE_STATISTICS)} />
        <Route path="/match-management" element={protect(<MatchManagement />, PERMISSIONS.MATCH_LIVE)} />
        <Route path="/matches-updates" element={protect(<MatchesUpdatesPage />, PERMISSIONS.MATCH_UPDATES)} />
        <Route path="/approvals" element={protect(<ApprovalsPage />, PERMISSIONS.ADMIN_APPROVALS)} />
        <Route path="/user-management" element={protect(<UserManagementPage />, PERMISSIONS.ADMIN_USERS)} />
        <Route path="/reports" element={protect(<ReportsPage />, PERMISSIONS.ADMIN_REPORTS)} />
        <Route path="/email-configuration" element={protect(<EmailConfigurationPage />, PERMISSIONS.ADMIN_EMAIL)} />
        <Route path="/finance-dashboard" element={protect(<FinanceDashboard />, PERMISSIONS.FINANCE_DASHBOARD)} />
        <Route path="/region-finance" element={protect(<RegionFinance />, PERMISSIONS.FINANCE_REGIONS)} />
        <Route path="/membership-records" element={protect(<MembershipRecords />, PERMISSIONS.FINANCE_MEMBERSHIPS)} />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
